import { useEffect, useCallback, useState } from "react";
import { Grid, Typography, Button, Box } from "@mui/material";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import CircularProgress from "@mui/material/CircularProgress";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import moment from "moment-timezone";
import chroma from "chroma-js";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";

interface ICrewBlockHoursProps {
  id: string;
  path: string[];
  crewId: string;
}

interface IBlockHours {
  year: number;
  month: number;
  hours: number;
}

export const CrewBlockHours = (props: ICrewBlockHoursProps): JSX.Element => {
  const { id, path, crewId } = props;
  const { cancellablePromise } = useCancellablePromise();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const [crewBlockHours, setCrewBlockHours] = useState<IBlockHours[]>([]);

  const getCrewBlockHours = useCallback(
    async (crewId: string): Promise<void> => {
      setIsLoading(true);
      setIsCancelled(false);
      const response = await AmplifyAPIWrapper.Get<IBlockHours[]>(
        `/crew/blockhours/${crewId}`,
        undefined,
        cancellablePromise
      );
      if (!isCancelled && Array.isArray(response)) {
        setCrewBlockHours(response);
      }
      setIsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancelled]
  );

  // Retrieve block hours for selected crew member.
  useEffect(() => {
    if (getCrewBlockHours && crewId) getCrewBlockHours(crewId);
    return () => {
      setIsCancelled(true); // cancelled call on unmount;
    };
  }, [getCrewBlockHours, crewId]);

  const currentMonth: number = new Date().getMonth() + 1; // return current month using 1 as Jan.

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={(theme) => ({
          backgroundColor: chroma(theme.palette.background.default).darken(0.5).css(),
          p: 0.5,
          pl: 2,
          borderBottom: `1px solid  ${chroma(theme.palette.background.default).brighten(0.5).css()}`,
          display: "flex",
          justifyContent: "space-between",
        })}
      >
        <Box>Crew Block Hours</Box>
        <Button
          id={"refresh_blockhours_btn"}
          onClick={() => getCrewBlockHours(crewId)}
          color="primary"
          disabled={isLoading}
          variant="contained"
          size="small"
          sx={{ py: 0, px: 1 }}
        >
          Refresh
        </Button>
      </Grid>

      {!isLoading ? (
        crewBlockHours?.map((block: IBlockHours, index: number) => {
          return (
            <Grid container spacing={1} columnSpacing={2} key={index}>
              <Grid item xs={5} sx={{ textAlign: "right", fontSize: "0.8em", fontWeight: 900 }}>{`${moment()
                .month(block?.month - 1)
                .format("MMM")}/${block?.year?.toString()}`}</Grid>
              <Grid item xs={7} sx={{ fontSize: "0.8em" }}>
                {block?.hours ? Math.round(block?.hours * 10) / 10 : "—"}
              </Grid>
            </Grid>
          );
        })
      ) : (
        <Grid item xs={12}>
          <LoadingWrapper message=" Loading Block Hours" messageSx={{ fontSize: "0.8em" }} />
        </Grid>
      )}
    </Grid>
  );
};
